import { WidgetConfig, WidgetData, WidgetAlert, HudSettings, defaultSettings } from './types';

// Local storage key for settings
const SETTINGS_KEY = 'umt-hud-settings';

// Load settings from localStorage
export function loadSettings(): HudSettings {
  try {
    const saved = localStorage.getItem(SETTINGS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Deep merge widgetUrls - use saved URL only if it's not empty, otherwise use default
      const mergedWidgetUrls = { ...defaultSettings.widgetUrls };
      if (parsed.widgetUrls) {
        for (const [key, value] of Object.entries(parsed.widgetUrls)) {
          const savedUrl = value as { api: string; drillDown: string };
          const defaultUrl = defaultSettings.widgetUrls[key] || { api: '', drillDown: '' };
          mergedWidgetUrls[key] = {
            api: savedUrl.api || defaultUrl.api,
            drillDown: savedUrl.drillDown || defaultUrl.drillDown,
          };
        }
      }
      // Merge widgetApiKeys
      const mergedApiKeys = { ...defaultSettings.widgetApiKeys, ...parsed.widgetApiKeys };
      return {
        ...defaultSettings,
        ...parsed,
        widgetUrls: mergedWidgetUrls,
        widgetApiKeys: mergedApiKeys,
      };
    }
  } catch (e) {
    console.error('Failed to load settings:', e);
  }
  return defaultSettings;
}

// Save settings to localStorage
export function saveSettings(settings: HudSettings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch (e) {
    console.error('Failed to save settings:', e);
  }
}

// Transform ITGlue API response to WidgetData
function transformITGlueResponse(data: {
  accessCount24h?: number;
  unknownIpCount?: number;
  activeTechs?: number;
  recentAlerts?: Array<{ severity: string; message: string; timestamp: string }>;
}): WidgetData {
  const alerts: WidgetAlert[] = (data.recentAlerts || []).map(alert => ({
    severity: alert.severity as 'info' | 'warning' | 'critical',
    message: alert.message,
    timestamp: alert.timestamp,
  }));

  // Determine overall status
  let status: WidgetData['status'] = 'healthy';
  if (data.unknownIpCount && data.unknownIpCount > 0) {
    status = 'warning';
  }
  if (alerts.some(a => a.severity === 'critical')) {
    status = 'critical';
  }

  return {
    status,
    lastUpdated: new Date().toISOString(),
    stats: [
      { label: 'Access (24h)', value: data.accessCount24h || 0, type: 'info' },
      { label: 'Unknown IPs', value: data.unknownIpCount || 0, type: data.unknownIpCount ? 'warning' : 'success' },
      { label: 'Active Techs', value: data.activeTechs || 0, type: 'info' },
    ],
    alerts,
  };
}

// Transform ScreenConnect API response to WidgetData
function transformScreenConnectResponse(data: {
  failedLogins24h?: number;
  ipsToBlock?: number;
  attacksToday?: number;
  recentAttacks?: Array<{ severity: string; message: string; timestamp: string }>;
}): WidgetData {
  const alerts: WidgetAlert[] = (data.recentAttacks || []).map(alert => ({
    severity: alert.severity as 'info' | 'warning' | 'critical',
    message: alert.message,
    timestamp: alert.timestamp,
  }));

  // Determine overall status
  let status: WidgetData['status'] = 'healthy';
  if (data.failedLogins24h && data.failedLogins24h > 10) {
    status = 'warning';
  }
  if (data.ipsToBlock && data.ipsToBlock > 0) {
    status = 'critical';
  }

  return {
    status,
    lastUpdated: new Date().toISOString(),
    stats: [
      { label: 'Failed Logins', value: data.failedLogins24h || 0, type: 'critical' },
      { label: 'IPs to Block', value: data.ipsToBlock || 0, type: 'orange' },
      { label: 'Attacks Today', value: data.attacksToday || 0, type: 'warning' },
    ],
    alerts,
  };
}

// Transform Server Health API response to WidgetData
// Response format: { success: true, data: { status, stats, alerts, lastUpdated } }
function transformServerHealthResponse(data: {
  success: boolean;
  data: {
    status: string;
    stats: Array<{ label: string; value: number; type?: string }>;
    alerts: Array<{ type: string; title: string; description: string }>;
    lastUpdated: string;
  };
}): WidgetData {
  const inner = data.data;
  return {
    status: inner.status as WidgetData['status'],
    lastUpdated: inner.lastUpdated,
    stats: inner.stats.map(s => ({
      label: s.label,
      value: s.value,
      type: s.type as 'info' | 'success' | 'warning' | 'critical' | undefined
    })),
    alerts: inner.alerts.map(a => ({
      severity: a.type as 'info' | 'warning' | 'critical',
      message: `${a.title}: ${a.description}`,
      timestamp: inner.lastUpdated
    }))
  };
}

// Transform AutoTask API response to WidgetData
function transformAutoTaskResponse(data: {
  success: boolean;
  data: {
    status: string;
    stats: Array<{ label: string; value: number; type?: string }>;
    alerts: Array<{ type: string; title: string; description: string }>;
    lastUpdated: string;
  };
}): WidgetData {
  const inner = data.data;
  return {
    status: inner.status as WidgetData['status'],
    lastUpdated: inner.lastUpdated,
    stats: inner.stats.map(s => ({
      label: s.label,
      value: s.value,
      type: s.type as 'info' | 'warning' | 'critical' | undefined
    })),
    alerts: inner.alerts.map(a => ({
      severity: a.type as 'info' | 'warning' | 'critical',
      message: `${a.title}: ${a.description}`,
      timestamp: inner.lastUpdated
    }))
  };
}

// Generic widget data transformer
function transformGenericResponse(data: {
  status?: string;
  stats?: Array<{ label: string; value: number | string; type?: string }>;
  alerts?: Array<{ severity: string; message: string; timestamp: string }>;
}): WidgetData {
  return {
    status: (data.status as WidgetData['status']) || 'healthy',
    lastUpdated: new Date().toISOString(),
    stats: (data.stats || []).map(s => ({
      label: s.label,
      value: s.value,
      type: s.type as 'info' | 'success' | 'warning' | 'critical',
    })),
    alerts: (data.alerts || []).map(a => ({
      severity: a.severity as 'info' | 'warning' | 'critical',
      message: a.message,
      timestamp: a.timestamp,
    })),
  };
}

// Fetch widget data
export async function fetchWidgetData(widget: WidgetConfig, settings?: HudSettings): Promise<WidgetData> {
  if (!widget.apiEndpoint) {
    // Return mock data for widgets without configured endpoints
    return {
      status: 'healthy',
      lastUpdated: new Date().toISOString(),
      stats: [
        { label: 'Status', value: 'Not Configured', type: 'info' },
      ],
      alerts: [],
    };
  }

  try {
    // Don't send credentials for external endpoints (CORS issue with Access-Control-Allow-Origin: *)
    const isExternal = widget.apiEndpoint.startsWith('http');

    // Build headers - add API key if widget requires authentication
    const headers: Record<string, string> = {};
    const currentSettings = settings || loadSettings();
    const apiKey = currentSettings.widgetApiKeys?.[widget.id];
    if (apiKey) {
      headers['X-API-Key'] = apiKey;
    }

    const response = await fetch(widget.apiEndpoint, {
      credentials: isExternal ? 'omit' : 'include',
      headers: Object.keys(headers).length > 0 ? headers : undefined,
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const data = await response.json();

    // Transform based on widget type
    switch (widget.id) {
      case 'itglue':
        return transformITGlueResponse(data);
      case 'screenconnect':
        return transformScreenConnectResponse(data);
      case 'serverhealth':
        return transformServerHealthResponse(data);
      case 'autotask':
        return transformAutoTaskResponse(data);
      default:
        return transformGenericResponse(data);
    }
  } catch (error) {
    console.error(`Failed to fetch ${widget.name} data:`, error);
    return {
      status: 'error',
      lastUpdated: new Date().toISOString(),
      stats: [],
      alerts: [],
    };
  }
}

// Format relative time
export function formatRelativeTime(timestamp: string): string {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  return `${diffDay}d ago`;
}
